import WaveSurfer from 'wavesurfer.js';
import { useEffect, useRef, useState } from 'react';

// Channel colors — used both in the waveform and the label overlays so they always match
const CHANNEL_COLORS = {
    ai:   { wave: '#16a34a', progress: '#14532d', bg: '#f0fdf4', border: '#86efac', text: '#15803d' },  // green
    user: { wave: '#ef4444', progress: '#7f1d1d', bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },  // red
};

const AudioWaveformPlayer = ({
    audioUrl,
    fileName = "audio_recording.wav",
    channelLabel = null,
    onTimeUpdate = null,
    seekTo = null,   // { seconds: number, id: any } — id changes ensure re-seek even at same position
    aiLabel = 'AI Assistant',
    userLabel = 'User',
    onRecordingUrlExpired = null,  // async () => freshUrl — recover from an expired presign
}) => {
    const waveformRef = useRef(null);
    const wavesurfer = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [loadingAudio, setLoadingAudio] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const isReady = useRef(false);
    // Web Audio context used to down-mix the hard-panned stereo recording to mono
    // for PLAYBACK (see wireMonoPlayback). The stereo waveform is untouched.
    const audioCtxRef = useRef(null);

    // Which label is on top/bottom
    const aiOnTop = !channelLabel || channelLabel.ai_channel === 0;
    const topColors    = aiOnTop ? CHANNEL_COLORS.ai   : CHANNEL_COLORS.user;
    const bottomColors = aiOnTop ? CHANNEL_COLORS.user : CHANNEL_COLORS.ai;
    const topLabel     = aiOnTop ? aiLabel : userLabel;
    const bottomLabel  = aiOnTop ? userLabel : aiLabel;

    const showLabels          = !!channelLabel;
    const showLowConfidence   = channelLabel?.confidence != null && channelLabel.confidence < 0.6;

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();
        isReady.current = false;
        setLoadingAudio(true);
        setLoadError(null);

        const init = async () => {
            if (!waveformRef.current || !audioUrl) return;

            if (wavesurfer.current) {
                try { wavesurfer.current.destroy(); } catch {}
            }

            // In wavesurfer v7 `splitChannels` IS the per-channel options array — there is
            // no `splitChannelsOptions` key (the library ignores it entirely). Passing the
            // old shape meant waveColor/progressColor were never applied and both channels
            // rendered in the default grey.
            wavesurfer.current = WaveSurfer.create({
                container:    waveformRef.current,
                barWidth:     3,
                barRadius:    3,
                barGap:       2,
                cursorWidth:  1,
                cursorColor:  '#6366f1',
                height:       40,
                splitChannels: [
                    { waveColor: topColors.wave,    progressColor: topColors.progress },
                    { waveColor: bottomColors.wave, progressColor: bottomColors.progress },
                ],
            });

            // ⚠️ Register ALL events BEFORE calling load().
            // In WaveSurfer v7, load() returns a Promise that resolves after 'ready' fires.
            // Listeners added after `await load()` would miss the 'ready' event entirely,
            // leaving isReady=false, duration=0, and seekTo broken.
            // Our call recordings are HARD-PANNED stereo (left = AI-under-test,
            // right = our test caller) so the waveform can show each speaker.
            // Played back raw, that means a listener on a single earbud / mono
            // output / a dead L or R channel hears only ONE party — which reads as
            // "no audio from the other side". Route playback through a Web Audio
            // graph that sums L+R into BOTH output channels (dual-mono) so both
            // parties are always audible. Best-effort: any failure leaves native
            // stereo playback intact. The waveform (splitChannels) is unaffected —
            // it reads the decoded peaks, not this graph.
            const wireMonoPlayback = () => {
                try {
                    const media = wavesurfer.current?.getMediaElement?.();
                    if (!media || media._monoWired) return;
                    const AC = window.AudioContext || window.webkitAudioContext;
                    if (!AC) return;
                    const ctx = new AC();
                    audioCtxRef.current = ctx;
                    const src = ctx.createMediaElementSource(media);
                    const splitter = ctx.createChannelSplitter(2);
                    const merger = ctx.createChannelMerger(2);
                    src.connect(splitter);
                    // sum both source channels into each of the two output channels
                    splitter.connect(merger, 0, 0);
                    splitter.connect(merger, 1, 0);
                    splitter.connect(merger, 0, 1);
                    splitter.connect(merger, 1, 1);
                    merger.connect(ctx.destination);
                    media._monoWired = true;
                } catch (e) {
                    console.warn('AudioWaveformPlayer: mono playback wiring failed, using native stereo', e);
                }
            };

            wavesurfer.current.on('ready', () => {
                if (!isMounted) return;
                isReady.current = true;
                wireMonoPlayback();
                setDuration(wavesurfer.current.getDuration());
                setLoadingAudio(false);
            });

            // 'timeupdate' fires during playback AND when the waveform is clicked / seeked.
            // 'audioprocess' only fires during playback — so we use 'timeupdate' for everything.
            wavesurfer.current.on('timeupdate', (time) => {
                setCurrentTime(time);
                if (onTimeUpdate) onTimeUpdate(time);
            });

            wavesurfer.current.on('play',   () => {
                // The context starts suspended until a user gesture; play IS one.
                audioCtxRef.current?.resume?.();
                setPlaying(true);
            });
            wavesurfer.current.on('pause',  () => setPlaying(false));
            wavesurfer.current.on('finish', () => {
                setPlaying(false);
                wavesurfer.current.setTime(0);  // resets cursor; timeupdate fires → currentTime=0
            });

            // Cache under the S3 object path, NOT the full presigned URL: the signature
            // changes on every request, so keying on it never hit the cache and instead
            // grew a new entry per page load.
            const cacheKey = (u) => {
                try { const { origin, pathname } = new URL(u); return origin + pathname; }
                catch { return u; }
            };

            const fetchAudio = async (url) => {
                const res = await fetch(url, { signal: abortController.signal });
                if (!res.ok) {
                    // S3 returns 403 for an expired signature, with an XML body. Reading
                    // that as audio is what used to leave the player blank forever.
                    const err = new Error(`HTTP ${res.status}`);
                    err.status = res.status;
                    throw err;
                }
                return res;
            };

            try {
                const cache = await caches.open('audio-recordings');
                const key = cacheKey(audioUrl);
                let response = await cache.match(key);
                if (!response) {
                    try {
                        response = await fetchAudio(audioUrl);
                    } catch (err) {
                        // Expired/forbidden signature — ask the parent for a fresh URL and
                        // retry once. Without this the player can never recover, because
                        // useMetricPolling deliberately pins the first presigned URL.
                        if (err.status === 403 && onRecordingUrlExpired) {
                            const freshUrl = await onRecordingUrlExpired();
                            if (!freshUrl) throw err;
                            response = await fetchAudio(freshUrl);
                        } else {
                            throw err;
                        }
                    }
                    await cache.put(key, response.clone());
                }
                const blob = await response.blob();
                await wavesurfer.current.loadBlob(blob);
            } catch (err) {
                if (err.name === 'AbortError' || !isMounted) return;
                console.error('WaveSurfer load error:', err);
                // Always leave the loading state — otherwise the component sits on a blank
                // waveform with no waveform, no error and no way out.
                setLoadingAudio(false);
                setLoadError(
                    err.status === 403
                        ? 'This recording link expired. Reload the page to play it.'
                        : 'Could not load the recording.'
                );
            }
        };

        init();

        return () => {
            isMounted = false;
            abortController.abort();
            isReady.current = false;
            try { wavesurfer.current?.destroy(); } catch {}
            try { audioCtxRef.current?.close(); } catch {}
            audioCtxRef.current = null;
        };
    }, [audioUrl, reloadKey]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Seek whenever seekTo changes (uses .id to allow re-seek at same position)
    useEffect(() => {
        if (!seekTo || !wavesurfer.current) return;

        const doSeek = () => {
            wavesurfer.current.setTime(seekTo.seconds);
            if (!wavesurfer.current.isPlaying()) {
                wavesurfer.current.play();
            }
        };

        if (isReady.current) {
            doSeek();
        } else {
            // Audio not ready yet — queue seek for when it is
            wavesurfer.current.once('ready', doSeek);
        }
    }, [seekTo]);  // eslint-disable-line react-hooks/exhaustive-deps

    const formatTime = (t) => {
        if (!t || isNaN(t)) return '0:00.000';
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60);
        const ms = Math.floor((t % 1) * 1000);
        return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    };

    return (
        <div className="bg-card">
            {loadingAudio && !loadError && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground py-2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Loading recording…
                </div>
            )}
            {loadError && (
                <div className="flex items-center justify-between gap-3 rounded px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200">
                    <span>{loadError}</span>
                    <button
                        onClick={() => setReloadKey((k) => k + 1)}
                        className="shrink-0 underline hover:no-underline"
                    >
                        Retry
                    </button>
                </div>
            )}
            {showLowConfidence && !loadingAudio && !loadError && (
                <div className="mb-2 rounded px-3 py-1.5 text-xs" style={{ background: '#fffbe6', border: '1px solid #faad14', color: '#856404' }}>
                    Low confidence channel detection ({Math.round(channelLabel.confidence * 100)}%). Labels may be inaccurate.
                </div>
            )}

            <div className="relative" style={(loadingAudio || loadError) ? { visibility: 'hidden', height: 0, overflow: 'hidden' } : {}}>
                {/* Channel label overlays — same colors as waveform */}
                {showLabels && (
                    <div style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-around',
                        pointerEvents: 'none', zIndex: 1, width: '72px',
                    }}>
                        <span style={{
                            background: topColors.bg, color: topColors.text,
                            border: `1px solid ${topColors.border}`,
                            fontSize: '10px', fontWeight: 700,
                            padding: '2px 6px', borderRadius: '4px',
                            display: 'inline-block', whiteSpace: 'nowrap',
                        }}>
                            {topLabel}
                        </span>
                        <span style={{
                            background: bottomColors.bg, color: bottomColors.text,
                            border: `1px solid ${bottomColors.border}`,
                            fontSize: '10px', fontWeight: 700,
                            padding: '2px 6px', borderRadius: '4px',
                            display: 'inline-block', whiteSpace: 'nowrap',
                        }}>
                            {bottomLabel}
                        </span>
                    </div>
                )}

                <div ref={waveformRef} style={showLabels ? { marginLeft: '76px' } : {}} />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-3" style={(loadingAudio || loadError) ? { display: 'none' } : {}}>
                <span className="text-xs font-mono text-muted-foreground">{formatTime(currentTime)}</span>
                <div className="flex items-center gap-2">
                    <a
                        href={audioUrl}
                        download={fileName}
                        className="px-3 py-1.5 text-xs border border-border rounded hover:bg-muted/50 text-muted-foreground"
                    >
                        Download
                    </a>
                    <div className="flex items-center gap-0.5 border border-border rounded overflow-hidden">
                        {[1, 1.5, 2].map((rate) => (
                            <button
                                key={rate}
                                onClick={() => {
                                    setPlaybackRate(rate);
                                    if (wavesurfer.current) wavesurfer.current.setPlaybackRate(rate);
                                }}
                                className={`px-2 py-1.5 text-xs font-medium transition-colors ${
                                    playbackRate === rate
                                        ? 'bg-indigo-600 text-white'
                                        : 'text-muted-foreground hover:bg-muted/50'
                                }`}
                            >
                                {rate}x
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => wavesurfer.current?.playPause()}
                        className="px-4 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                    >
                        {playing ? 'Pause' : 'Play'}
                    </button>
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                    {duration > 0 ? formatTime(duration) : '--:--'}
                </span>
            </div>

        </div>
    );
};

export default AudioWaveformPlayer;
